'use client'

import { Box, Heading, Text, Icon } from '@chakra-ui/react'
import { ContentContainer } from 'ui'
import { keyframes } from '@emotion/react'
import { FC } from 'react'
import { GrandButton } from '../_components/granditude'
import {
  FaExclamationTriangle,
  FaClock,
  FaBell,
  FaGlobe,
  FaBriefcase,
  FaLock,
  FaFile,
  FaUsers,
  FaChartLine,
  FaFrown
} from 'react-icons/fa'
import { IconType } from 'react-icons'

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// For the H1 and intro text: an element starting at opacity 0 doesn't count as painted, so
// fading in the H1 pushed Largest Contentful Paint back (8.7 s measured). Movement only.
const riseUp = keyframes`
  from {
    transform: translateY(20px);
  }
  to {
    transform: translateY(0);
  }
`

type ChaosCard = {
  icon: IconType
  text: string
  width: string
  top: string
  right: string
  rotate: string
  opacity: number
  hideOnMedium?: boolean
}

const chaosCards: ChaosCard[] = [
  {
    icon: FaExclamationTriangle,
    text: '3+ systems "integrated" but inventory is still wrong',
    width: '195px',
    top: '6%',
    right: '8%',
    rotate: '-4deg',
    opacity: 0.5
  },
  {
    icon: FaClock,
    text: 'Hours spent on manual work that should be automated',
    width: '210px',
    top: '12%',
    right: '32%',
    rotate: '3deg',
    opacity: 0.65,
    hideOnMedium: true
  },
  {
    icon: FaBell,
    text: 'Outgrown Shopify but Magento is not an option',
    width: '180px',
    top: '28%',
    right: '5%',
    rotate: '5deg',
    opacity: 0.6
  },
  {
    icon: FaGlobe,
    text: 'New market expansion feels like a 6-month project',
    width: '200px',
    top: '26%',
    right: '24%',
    rotate: '-2deg',
    opacity: 0.55,
    hideOnMedium: true
  },
  {
    icon: FaBriefcase,
    text: 'B2B customers still ordering by email',
    width: '185px',
    top: '46%',
    right: '10%',
    rotate: '-5deg',
    opacity: 0.7
  },
  {
    icon: FaLock,
    text: 'Locked into tools that limit what you can do',
    width: '205px',
    top: '50%',
    right: '28%',
    rotate: '4deg',
    opacity: 0.5,
    hideOnMedium: true
  },
  {
    icon: FaFile,
    text: 'Product data scattered across 5 different spreadsheets',
    width: '190px',
    top: '66%',
    right: '4%',
    rotate: '3deg',
    opacity: 0.6
  },
  {
    icon: FaUsers,
    text: 'Only one person knows how the systems actually work',
    width: '215px',
    top: '70%',
    right: '22%',
    rotate: '-4deg',
    opacity: 0.45,
    hideOnMedium: true
  },
  {
    icon: FaChartLine,
    text: "No visibility into what's selling until month-end",
    width: '175px',
    top: '84%',
    right: '12%',
    rotate: '6deg',
    opacity: 0.55
  },
  {
    icon: FaFrown,
    text: 'Customers cancelling because stock was wrong',
    width: '195px',
    top: '88%',
    right: '35%',
    rotate: '-3deg',
    opacity: 0.4,
    hideOnMedium: true
  }
]

export const HeroSection: FC = () => {
  return (
    <Box
      mt={-10}
      as="section"
      w="full"
      minH="100vh"
      display="flex"
      alignItems="center"
      pt={{ base: 'calc(80px + 6rem)', md: 'calc(80px + 4rem)' }}
      pb={{ base: 16, md: 24 }}
      px={{ base: 4, md: 10 }}
      position="relative"
      overflow="hidden"
      bgGradient={{
        base: 'linear(to-r, background.default, background.default)',
        lg: 'linear(105deg, background.default 0%, background.default 35%, background.surface 50%, #d5dde0 65%, #3a444f 85%, background.dark 100%)'
      }}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        right: '-20%',
        width: '70%',
        height: '100%',
        bg: 'radial-gradient(ellipse at center, rgba(27, 133, 145, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none'
      }}
    >
      {/* Chaos Cards Background - only show on large screens */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        pointerEvents="none"
        zIndex={1}
        display={{ base: 'none', lg: 'block' }}
      >
        {chaosCards.map((card, index) => (
          <Box
            key={index}
            position="absolute"
            bg="background.default"
            border="1px solid"
            borderColor="whiteAlpha.300"
            borderRadius="8px"
            p={3}
            boxShadow="chaos"
            fontSize="0.78rem"
            lineHeight={1.4}
            color="text.secondary"
            w={card.width}
            top={card.top}
            right={card.right}
            transform={`rotate(${card.rotate})`}
            opacity={card.opacity}
            display={{ base: 'none', lg: card.hideOnMedium ? 'none' : 'block', xl: 'block' }}
            transition="opacity 0.3s ease, transform 0.3s ease"
            _hover={{ opacity: 1, zIndex: 10 }}
          >
            <Icon
              as={card.icon}
              w={4}
              h={4}
              color="teal.500"
              mb={1}
              opacity={0.7}
              display="block"
            />
            {card.text}
          </Box>
        ))}
      </Box>

      <ContentContainer position="relative" zIndex={2}>
        <Box maxW="650px">
          <Text
            as="span"
            display="inline-block"
            fontSize="0.85rem"
            fontWeight={600}
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="teal.500"
            mb={6}
            animation={`${fadeInUp} 0.6s ease forwards 0.1s`}
            opacity={0}
          >
            Granditude Commerce
          </Text>

          <Heading
            as="h1"
            fontFamily="heading"
            fontSize={{ base: '2.2rem', md: '3.5rem', lg: '4.2rem' }}
            fontWeight={500}
            lineHeight={1.15}
            letterSpacing="-0.03em"
            mb={10}
            color="text.default"
            animation={`${riseUp} 0.6s ease both`}
          >
            Run your ecommerce{' '}
            <Box as="em" fontStyle="italic" color="teal.500">
              without the chaos.
            </Box>
          </Heading>

          <Text
            fontSize={{ base: '1.1rem', md: '1.2rem' }}
            lineHeight={1.7}
            color="text.secondary"
            maxW="550px"
            mb={10}
            animation={`${riseUp} 0.6s ease both`}
          >
            We work with businesses to build, connect, and run their ecommerce — you&apos;re not on
            your own.
          </Text>

          <Box animation={`${fadeInUp} 0.6s ease forwards 0.4s`} opacity={0}>
            <GrandButton href="/signup" variant="primary" showArrow>
              Let&apos;s figure it out together
            </GrandButton>
          </Box>
        </Box>
      </ContentContainer>
    </Box>
  )
}
